import { useState, useEffect, useRef } from "react";
import CTProductCard from "../../components/CTProductCard";
import {
  MotifProgressLoader,
  MotifPagination,
  MotifFilters,
  MotifFormField,
  MotifCheckbox,
  MotifAccordion,
  MotifAccordionContent,
  MotifAccordionTrigger,
  MotifButton,
  MotifSearch,
  MotifIcon,
  MotifSelect,
  MotifOption,
  MotifBadge,
  MotifToast,
  MotifErrorMessage,
} from "@ey-xd/motif-react";
import { useSelector, useDispatch } from "react-redux";
import {
  resetProductState,
  fetchProductListData,
} from "../../store/slices/CTDBProductListSlice";
import { RootState, AppDispatch } from "../../store";
import "./CTHome.scss";
import { fetchProxyOwnerListData } from "../../store/slices/CTDBProxyOwnerListSlice";
import { IconoirFilterList } from "@ey-xd/motif-icon";
import {
  assignProxy,
  resetAssignProxyState,
} from "../../store/slices/CTDBAssignProxySlice";
import { containsEmoji } from "../../utils/common";
import { EMOJI_ERROR_MESSAGE } from "../../Constant";

const CTHome = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const ref = useRef<HTMLDivElement>(null);
  const [searchFilter, setSearchFilter] = useState("");
  const [accordions, setAccordions] = useState<
    { title: string; checkboxes: string[] }[]
  >([]);
  const [accordionsDet, setAccordionDet] = useState<
    {
      accordion: string;
      id: number;
      checkboxes: {
        name: string;
        checked: boolean;
        id: number;
      }[];
    }[]
  >([]);
  const [selectAll, setSelectAll] = useState<number[]>([]);
  const [badge, setBadge] = useState(0);
  const [notResult, setNotResult] = useState<any>(false);
  const [openAccordionState, setOpenAccordionState] = useState<any>(null);
  const [openAccordionClosed, setOpenAccordionClosed] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [accordionsDetApplied, setAccordionDetApplied] = useState<
    {
      accordion: string;
      id: number;
      checkboxes: {
        name: string;
        checked: boolean;
        id: number;
      }[];
    }[]
  >([]);

  const [showToastSuccess, setShowToastSuccess] = useState(false);
  const [showToastError, setShowToastError] = useState(false);

  const isLoading = useSelector(
    (state: RootState) => state.CTDBProductListSlice.isLoading
  );
  const data = useSelector(
    (state: RootState) => state.CTDBProductListSlice.data
  );
  const error = useSelector(
    (state: RootState) => state.CTDBProductListSlice.error
  );

  const userEmail: string = useSelector(
    (state: RootState) => state.UserProfileSlice.emailId
  );

  const proxyOwnerData = useSelector(
    (state: RootState) => state.CTDBProxyOwnerListSlice.data
  );

  const userPermissionsData: string[] = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissions
  );

  const [searchValidationError, setSearchValidationError] = useState<string>("");

  useEffect(() => {
    dispatch(resetProductState());
    dispatch(fetchProxyOwnerListData());
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: page,
        record: pageSize,
        portfolio: [],
        productProxyOwner: [],
      })
    );
  }, []);

  useEffect(() => {
    if (!isOpen) {
      if (!deepCompare(accordionsDet, accordionsDetApplied)) {
        const proxyOwnerList: number[] = [];
        accordionsDet.map((acc) => {
          if (acc.accordion == "Com. Proxy Owner") {
            acc.checkboxes
              .filter((chk) => chk.checked === true)
              .map((chk) => {
                if (getIdFromEmail(chk.name) !== 0) {
                  proxyOwnerList.push(getIdFromEmail(chk.name));
                }
              });
          }
        });

        handleFilterClose(proxyOwnerList);
      }
    }
  }, [isOpen]);

  const getIdFromEmail = (email: string) => {
    const proxyOwner = proxyOwnerData
      ? proxyOwnerData.find((item) => item.Email === email)
      : null;
    return proxyOwner ? proxyOwner.UserId : 0;
  };

  useEffect(() => {
    if (proxyOwnerData && proxyOwnerData.length > 0) {
      const checkboxesList: string[] = [];
      proxyOwnerData.forEach(
        (item: { UserId: number; Name: string; Email: string }) => {
          checkboxesList.push(item.Email);
        }
      );
      if (
        userPermissionsData.filter(
          (permission: string) => permission === "product_commowner"
        ).length > 0
      ) {
        setAccordions([
          {
            title: "Com. Proxy Owner",
            checkboxes: checkboxesList,
          },
        ]);
      }
    }
  }, [proxyOwnerData]);

  useEffect(() => {
    if (accordions.length > 0) {
      const newState = accordions.map((accord, index) => {
        const checkboxes = accord.checkboxes.map((chk, indexChk) => ({
          name: chk,
          checked: false,
          id: indexChk,
        }));

        return { accordion: accord.title, id: index, checkboxes };
      });
      setAccordionDet(newState);
    }
  }, [accordions]);

  useEffect(() => {
    const sumAcc = accordionsDet.reduce((sum, p) => {
      const sumCheck = p.checkboxes.reduce(
        (sum2, c) => (c.checked === true ? sum2 + 1 : sum2),
        0
      );

      return sum + sumCheck;
    }, 0);
    setBadge(sumAcc);
  }, [accordionsDet]);

  useEffect(() => {
    const notResultArray = accordionsDet.map((accDetail) =>
      accDetail.checkboxes.some((chk) =>
        chk.name.toLowerCase().includes(searchFilter.toLowerCase())
      )
    );
    setNotResult(
      accordionsDet.length && notResultArray.every((result) => result !== true)
    );
    if (searchFilter.length) {
      setOpenAccordionState(true);
    }

    return () => {
      setOpenAccordionState(null);
      setOpenAccordionClosed([]);
    };
  }, [searchFilter, accordionsDet]);

  const handleSearch = () => {
    const proxyOwnerList: number[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Com. Proxy Owner") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            if (getIdFromEmail(chk.name) !== 0) {
              proxyOwnerList.push(getIdFromEmail(chk.name));
            }
          });
      }
    });
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: 1,
        record: pageSize,
        portfolio: [],
        productProxyOwner: proxyOwnerList,
      })
    );
  };

  const handleSearchClear = () => {
    setSearchInput("");
    const proxyOwnerList: number[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Com. Proxy Owner") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            if (getIdFromEmail(chk.name) !== 0) {
              proxyOwnerList.push(getIdFromEmail(chk.name));
            }
          });
      }
    });
    dispatch(
      fetchProductListData({
        search: "",
        page: 1,
        record: pageSize,
        portfolio: [],
        productProxyOwner: proxyOwnerList,
      })
    );
  };

  const deepCompare = (obj1: any, obj2: any) => {
    if (
      typeof obj1 !== "object" ||
      obj1 === null ||
      typeof obj2 !== "object" ||
      obj2 === null
    ) {
      return obj1 === obj2;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (!keys2.includes(key) || !deepCompare(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setAccordionDetApplied(accordionsDet);
    }
  };

  const handleClick = ({
    accordionId,
    checkboxId,
  }: {
    accordionId: number;
    checkboxId: number;
  }) => {
    const accordionsDetCopy = accordionsDet.map((accDetail) => {
      if (accDetail.id === accordionId) {
        const newAccordion = {
          ...accDetail,
          // eslint-disable-next-line no-confusing-arrow
          checkboxes: accDetail.checkboxes.map((checkbox) =>
            checkbox.id === checkboxId
              ? { ...checkbox, checked: !checkbox.checked }
              : checkbox
          ),
        };

        return newAccordion;
      }

      return accDetail;
    });
    setAccordionDet(accordionsDetCopy);
  };

  const toggleSelectAll = ({ accordionId }: { accordionId: number }) => {
    const newList = selectAll.some((item) => item === accordionId)
      ? selectAll.filter((state) => state !== accordionId)
      : [...selectAll, accordionId];

    // eslint-disable-next-line no-confusing-arrow
    const accordionsDetCopy = accordionsDet.map((acc) =>
      acc.id === accordionId
        ? {
            ...acc,
            checkboxes: acc.checkboxes.map((chk) => ({
              ...chk,
              checked: !selectAll.some((item) => item === accordionId),
            })),
          }
        : acc
    );
    setSelectAll(newList);
    setAccordionDet(accordionsDetCopy);
  };

  const textItem = (str: string) => {
    const parts = str.split(new RegExp(`(${searchFilter})`, "gi"));

    return (
      <span>
        {parts.map((part, ind) => (
          <span
            key={ind}
            style={
              part.toLowerCase() === searchFilter.toLowerCase()
                ? { fontWeight: "bold" }
                : {}
            }
          >
            {part}
          </span>
        ))}
      </span>
    );
  };

  const clearAll = () => {
    const accordionsDetCopy = accordionsDet.map((acc) => ({
      ...acc,
      checkboxes: acc.checkboxes.map((chk) => ({ ...chk, checked: false })),
    }));
    setAccordionDet(accordionsDetCopy);
    setOpenAccordionState(null);
    setOpenAccordionClosed([]);
    setSelectAll([]);
    setSearchFilter("");
  };

  // eslint-disable-next-line
  const onCloseAccordion = (id: any) =>
    openAccordionState
      ? setOpenAccordionClosed(openAccordionClosed.concat(id))
      : false;

  const handleFilterClose = (proxyOwnerList: number[]) => {
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: 1,
        record: pageSize,
        portfolio: [],
        productProxyOwner: proxyOwnerList,
      })
    );
  };

  const handlePageChange = (page: number | string) => {
    setPage(Number(page));
    const proxyOwnerList: number[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Com. Proxy Owner") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            if (getIdFromEmail(chk.name) !== 0) {
              proxyOwnerList.push(getIdFromEmail(chk.name));
            }
          });
      }
    });
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: Number(page),
        record: pageSize,
        portfolio: [],
        productProxyOwner: proxyOwnerList,
      })
    );
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
    const proxyOwnerList: number[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Com. Proxy Owner") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            if (getIdFromEmail(chk.name) !== 0) {
              proxyOwnerList.push(getIdFromEmail(chk.name));
            }
          });
      }
    });
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: 1,
        record: pageSize,
        portfolio: [],
        productProxyOwner: proxyOwnerList,
      })
    );
  };

  const refreshPage = () => {
    const proxyOwnerList: number[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Com. Proxy Owner") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            if (getIdFromEmail(chk.name) !== 0) {
              proxyOwnerList.push(getIdFromEmail(chk.name));
            }
          });
      }
    });
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: page,
        record: pageSize,
        portfolio: [],
        productProxyOwner: proxyOwnerList,
      })
    );
  };

  const handleUpdateProxyOwners = async (
    productId: string,
    proxyOwners: { Name: string; Email: string }[]
  ) => {
    try {
      const response = await dispatch(assignProxy({ productId, proxyOwners }));
      if (response.payload.Status === "success") {
        dispatch(resetAssignProxyState());
        // refetch the proxy owner list
        dispatch(fetchProxyOwnerListData());
        refreshPage();
        setShowToastSuccess(true);
        setTimeout(() => {
          setShowToastSuccess(false);
        }, 5000);
        return;
      }
    } catch (error) {
      setShowToastError(true);
      setTimeout(() => {
        setShowToastError(false);
      }, 5000);
      console.log("Error in update proxy owners", error);
    }
  };

  const displayFilter = () => {
    if (userPermissionsData && userPermissionsData.length > 0) {
      if (
        userPermissionsData.filter(
          (permission: string) => permission === "product_commowner"
        ).length > 0
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  return (
    <div className="cthome-container-wrapper">
      <section className="cthome-container">
        <div className="cthome-content">
          <h1 className="cthome-title">SKU Database</h1>
          <p className="cthome-description">
            The SKU database serves as a centralized catalog
            designed to store and manage comprehensive information about each
            product and asset. Each SKU is assigned a unique
            code, enabling clear differentiation among various purchase options
            available to customers.
            <br />
            <br />
            This database not only includes essential cost and billing
            information but also integrates seamlessly with the Commercial Hub,
            empowering users to estimate and order products efficiently.
            Additionally, the SKU database provides valuable insights for
            product owners by allowing them to easily track and analyze purchase
            trends, facilitating informed decision-making and strategic
            planning.
          </p>
        </div>
      </section>
      <section className="cthome-products" ref={ref}>
        {isLoading && (
          <MotifProgressLoader
            aria-busy="true"
            aria-label="This page is loading"
            aria-live="polite"
            arialabel="This page is loading"
            show
            variant="default"
            className="cthome-progress"
          />
        )}

        {!isLoading && (
          <div className="cthome-product-content">
            <h2 className="cthome-product-pre-title">SERVICES</h2>
            <h2 className="cthome-product-title">Technology Products</h2>
            <p className="cthome-product-description">
              Manage technology assets and their associated SKUs
            </p>
            <div className="cthome-product-search-container">
              <MotifSearch
                type="text"
                placeholder="Search"
                className="cthome-product-search"
                value={searchInput}
                onChange={(e) => {
                  const isContainEmoji = containsEmoji(e.target.value);
                  if (isContainEmoji) {
                    setSearchValidationError(EMOJI_ERROR_MESSAGE);
                  } else {
                    setSearchValidationError("");
                  }
                  setSearchInput(e.target.value);
                  if (e.target.value === "") {
                    handleSearchClear();
                  }
                }}
                onEnter={() => {
                  if (searchValidationError == "") {
                    handleSearch()
                  }
                }}
                onClear={() => handleSearchClear()}
              ></MotifSearch>                   
              <div className={displayFilter() ? "" : "cthome-filter-hidden"}>
                <MotifButton
                  className="cthome-product-filter"
                  id="dropdown-trigger-1"
                  onClick={toggleDropdown}
                  aria-label="filter"
                >
                  Filter
                  <MotifIcon
                    size={"20"}
                    iconFunction={IconoirFilterList}
                    className="motif-filter-icon"
                  />
                </MotifButton>

                {isOpen && (
                  <MotifFilters
                    size="default"
                    className="cthome-filter-dropdown"
                  >
                    <header>
                      <div>
                        <span>
                          Filters
                          {badge ? (
                            <MotifBadge variant={"info"} value={badge} />
                          ) : null}
                        </span>
                        <MotifButton
                          onClick={() => clearAll()}
                          style={{
                            padding: "0",
                          }}
                          variant="text"
                          disabled={!badge && !searchFilter}
                          className={
                            !badge && !searchFilter ? "clear-all-disabled" : ""
                          }
                        >
                          Clear
                        </MotifButton>
                      </div>
                    </header>
                    <MotifFormField>
                      <MotifSearch
                        value={searchFilter}
                        aria-label="Search"
                        onClear={() => setSearchFilter("")}
                        onChange={(event) => {
                          setSearchFilter(event.target.value);
                        }}
                        placeholder="Search"
                      />
                    </MotifFormField>
                    <div className="accordion-wrapper">
                      {notResult ? (
                        <span style={{ marginLeft: "19px" }}>
                          No results were found
                        </span>
                      ) : (
                        accordionsDet.map((accDetail) =>
                          accDetail.checkboxes.some((chk) =>
                            chk.name
                              .toLowerCase()
                              .includes(searchFilter.toLowerCase())
                          ) ? (
                            <MotifAccordion
                              key={accDetail.id}
                              onOpen={() => null}
                              onClose={() => {
                                onCloseAccordion(accDetail.id);
                              }}
                              alignIconRight={true}
                              useChevronIcon={true}
                            >
                              <MotifAccordionTrigger>
                                {accDetail.accordion}
                              </MotifAccordionTrigger>
                              <MotifAccordionContent>
                                {!searchFilter && (
                                  <>
                                    <MotifFormField className="motif-select-all-options">
                                      <MotifCheckbox
                                        id={`checkbox-selectAll-${accDetail.id}`}
                                        value="checkbox-example"
                                        name="checkbox-example"
                                        indeterminate={
                                          selectAll.some(
                                            (item) => item === accDetail.id
                                          ) &&
                                          accDetail.checkboxes.some(
                                            (item) => item.checked === false
                                          )
                                        }
                                        checked={selectAll.some(
                                          (item) => item === accDetail.id
                                        )}
                                        onChange={() => {
                                          toggleSelectAll({
                                            accordionId: accDetail.id,
                                          });
                                        }}
                                      >
                                        {selectAll.some(
                                          (item) => item === accDetail.id
                                        )
                                          ? "Deselect all"
                                          : "Select all"}
                                      </MotifCheckbox>
                                    </MotifFormField>
                                  </>
                                )}
                                {accDetail.checkboxes.map((chk) =>
                                  chk.name
                                    .toLowerCase()
                                    .includes(searchFilter.toLowerCase()) ? (
                                    <MotifFormField key={chk.id}>
                                      <MotifCheckbox
                                        id={`checkbox-${accDetail.accordion}-${chk.id}`}
                                        value="checkbox-example"
                                        name="checkbox-example"
                                        checked={chk.checked}
                                        onChange={() => {
                                          handleClick({
                                            accordionId: accDetail.id,
                                            checkboxId: chk.id,
                                          });
                                        }}
                                      >
                                        {textItem(chk.name)}
                                      </MotifCheckbox>
                                    </MotifFormField>
                                  ) : null
                                )}
                              </MotifAccordionContent>
                            </MotifAccordion>
                          ) : null
                        )
                      )}
                    </div>
                  </MotifFilters>
                )}
              </div>
            </div>
             <div>
                {searchValidationError && (
                  <MotifErrorMessage className="search-helper-text">
                      {searchValidationError}
                  </MotifErrorMessage>
                )}
              </div>
            {data && !error && data.Data && data.Data.length > 0 && (
              <>
                <div className="cthome-productlist">
                  {data.Data.map((product: any) => (
                    <CTProductCard
                      key={product.ProductId}
                      ProductId={product.ProductId}
                      Name={product.Name}
                      Description={product.Description}
                      ProxyOwner={product.ProxyOwner}
                      ProductOwner={product.ProductOwner}
                      handleUpdateProxyOwners={handleUpdateProxyOwners}
                      userEmail={userEmail}
                    />
                  ))}
                </div>
                <div className="cthome-pagination">
                  <div className="cthome-pagination-text">Show</div>
                  <MotifFormField className="cthome-select-pageSize">
                    <MotifSelect
                      ariaLabelledBy="select-label"
                      onChange={(pageSize) =>
                        handlePageSizeChange(Number(pageSize))
                      }
                      value={String(pageSize)}
                      visibleOptions={3}
                    >
                      <MotifOption value="10">10</MotifOption>
                      <MotifOption value="20">20</MotifOption>
                      <MotifOption value="50">50</MotifOption>
                    </MotifSelect>
                  </MotifFormField>
                  <MotifPagination
                    allowInput={false}
                    aria-label="Pagination Navigation"
                    currentPage={data.Page}
                    firstButtonProps={{
                      title: "Go to first page",
                    }}
                    lastButtonProps={{
                      title: "Go to last page",
                    }}
                    max={data.TotalPages}
                    min={1}
                    nextButtonProps={{
                      title: "Go to next page",
                    }}
                    onPageChange={(page: number | string) =>
                      handlePageChange(page)
                    }
                    prevButtonProps={{
                      title: "Go to previous page",
                    }}
                    role="navigation"
                  />
                </div>
                {showToastSuccess && (
                  <MotifToast variant="success">
                    <div slot="toast-supporting-text">
                      Thanks for your input, Commercial Proxy Owner updated
                      successfully !!
                    </div>
                  </MotifToast>
                )}
                {showToastError && (
                  <MotifToast variant="error">
                    <div slot="toast-supporting-text">
                      Error updating Proxy Owner, please try again later.
                    </div>
                  </MotifToast>
                )}
              </>
            )}
            {data && !error && data.Data && data.Data.length === 0 && (
              <div className="cthome-empty-productlist">
                <span>No products found matching your criteria.</span>
              </div>
            )}
            {!data && error && (
              <div
                className="cthome-empty-productlist"
                style={{ color: "red" }}
              >
                <span>Error while fetching data, please refresh.</span>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default CTHome;
